import { useEffect, useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';
import { CornerDownLeft, Search } from 'lucide-react';
import { useNavigate } from 'react-router';
import type { ThreadSummary } from '@rundown/types';
import { cx } from '../../lib/cx';
import { formatRelativeTime } from '../notifications/relativeTime';
import { useRequiredWorkspace } from '../workspace/useWorkspace';
import { threadSearchQueryOptions } from './api';
import { KindBadge } from './ThreadSidebar';

const RESULT_LIMIT = 8;

/** How anything outside the palette (the header button) opens it. */
const OPEN_EVENT = 'rundown:open-command-palette';

/**
 * The header's way into search: a small button that opens the palette and
 * teaches its shortcut, beside the bell. The palette is the one search in the
 * app; this only says where it is.
 */
export function SearchButton() {
  const shortcut = paletteShortcutLabel();
  return (
    <button
      type="button"
      onClick={() => window.dispatchEvent(new Event(OPEN_EVENT))}
      aria-label={`Search (${shortcut})`}
      title={`Search threads and tickets (${shortcut})`}
      className="flex items-center gap-2 rounded-lg border border-line bg-surface-2 py-1.5 pr-1.5 pl-2.5 fs-12.5 text-muted hover:border-line-strong hover:text-ink"
    >
      <Search size={14} strokeWidth={2} aria-hidden />
      <span className="hidden sm:inline">Search</span>
      <kbd className="rounded border border-line bg-base px-1.5 fs-10.5 font-medium text-muted">
        {shortcut}
      </kbd>
    </button>
  );
}

/** ⌘K on a Mac, Ctrl+K everywhere else — the label to show beside a search box. */
export function paletteShortcutLabel(): string {
  const platform =
    (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ??
    navigator.platform;
  return /mac|iphone|ipad/i.test(platform) ? '⌘K' : 'Ctrl K';
}

/**
 * Jump to any thread or ticket from anywhere: ⌘K on a Mac, Ctrl+K on Windows
 * and Linux.
 *
 * Words match titles, opening posts and replies; "#41" or "41" goes straight to
 * that issue. With nothing typed it lists what was active most recently, which
 * is usually where somebody wants to go. Arrow keys move, Enter opens, Esc
 * closes. Archived threads are found too — searching is how you get back to
 * one.
 */
export function CommandPalette() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 'k') {
        // Ctrl+K is the browser's own "search the web" in Chrome and Firefox
        // on Windows; inside the app this is what it means instead.
        event.preventDefault();
        setOpen((current) => !current);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/25" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed top-[12vh] left-1/2 z-50 w-[min(40rem,calc(100vw-2rem))] -translate-x-1/2 overflow-hidden rounded-xl border border-line bg-base shadow-[0_24px_60px_-20px_rgba(0,0,0,0.35)]"
        >
          <Dialog.Title className="sr-only">Go to a thread</Dialog.Title>
          {open && <PaletteBody onDone={() => setOpen(false)} />}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function PaletteBody({ onDone }: { onDone: () => void }) {
  const { workspace } = useRequiredWorkspace();
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [search, setSearch] = useState('');
  const [highlighted, setHighlighted] = useState(0);

  // A short pause, not per keystroke — the list keeps the last results while
  // the next ones load, so typing never flashes an empty panel.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(text.trim()), 120);
    return () => clearTimeout(timer);
  }, [text]);

  const query = useQuery({
    ...threadSearchQueryOptions(workspace.id, search, RESULT_LIMIT),
    placeholderData: (previous) => previous,
  });
  const results: readonly ThreadSummary[] = useMemo(() => query.data ?? [], [query.data]);
  const current = Math.min(highlighted, Math.max(results.length - 1, 0));

  const go = (thread: ThreadSummary | undefined) => {
    if (thread === undefined) return;
    onDone();
    void navigate(`/t/${thread.id}`);
  };

  return (
    <div>
      <div className="flex items-center gap-2.5 border-b border-line px-4">
        <Search size={16} strokeWidth={2} aria-hidden className="flex-none text-muted" />
        <input
          autoFocus
          value={text}
          onChange={(event) => {
            setText(event.target.value);
            setHighlighted(0);
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setHighlighted((index) => Math.min(index + 1, results.length - 1));
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              setHighlighted((index) => Math.max(index - 1, 0));
            } else if (event.key === 'Enter') {
              event.preventDefault();
              go(results[current]);
            }
          }}
          placeholder="Jump to a thread or #number…"
          aria-label="Search threads"
          role="combobox"
          aria-expanded={results.length > 0}
          aria-controls="palette-results"
          aria-activedescendant={results[current] ? `palette-${results[current].id}` : undefined}
          className="min-w-0 flex-1 bg-transparent py-3.5 fs-15 text-ink placeholder:text-faint focus:outline-none"
        />
      </div>

      <ul id="palette-results" role="listbox" className="max-h-[50vh] overflow-y-auto p-1.5">
        {results.length === 0 ? (
          <li className="px-3 py-6 text-center fs-12.5 text-muted">
            {query.isFetching
              ? 'Searching…'
              : search === ''
                ? 'No threads yet.'
                : `Nothing matches “${search}”.`}
          </li>
        ) : (
          results.map((thread, index) => (
            <li
              key={thread.id}
              id={`palette-${thread.id}`}
              role="option"
              aria-selected={index === current}
              onMouseEnter={() => setHighlighted(index)}
              onMouseDown={(event) => {
                event.preventDefault();
                go(thread);
              }}
              className={cx(
                'flex cursor-pointer items-center gap-2.5 rounded-lg px-3 py-2',
                index === current ? 'bg-surface-3' : '',
              )}
            >
              <KindBadge thread={thread} />
              <span className="min-w-0 flex-1">
                <span
                  className={cx('block truncate fs-13', thread.unread ? 'font-bold text-ink' : 'text-ink')}
                >
                  {thread.title}
                </span>
                {thread.lastMessage !== null && (
                  <span className="block truncate fs-11.5 text-muted">
                    {thread.lastMessage.authorKind === 'bot' ? '@rundown' : thread.lastMessage.authorName}:{' '}
                    {thread.lastMessage.snippet}
                  </span>
                )}
              </span>
              <span className="flex-none fs-10.5 text-faint">
                {formatRelativeTime(thread.lastActivityAt)}
              </span>
              {index === current && (
                <CornerDownLeft size={13} strokeWidth={2} aria-hidden className="flex-none text-muted" />
              )}
            </li>
          ))
        )}
      </ul>

      <div className="flex items-center gap-3 border-t border-line px-4 py-2 fs-11 text-faint">
        <span>↑↓ to move</span>
        <span>↵ to open</span>
        <span>esc to close</span>
        <span className="ml-auto">{search === '' ? 'Recent activity' : 'Titles, messages and #numbers'}</span>
      </div>
    </div>
  );
}
